import { Btn, DataTable, Stack, TEIQspinner } from "./ui";
import type { DataTableColumn } from "./ui";
import * as Sentry from "@sentry/browser";
import { DisplayStatusIcon } from "./DisplayStatusIcon";
import { DEFAULT_FORM_INPUT_TYPES, LOCAL_STORAGE_KEYS, S3_IMAGE_PATH, REPORT_TYPES } from "../utils/constants";
import { iShiftReportReviewProps } from "../types";
import SignaturePad from 'signature_pad';
import { useEffect, useRef, useState } from "react";
import styled from '@emotion/styled';
import { base64ToBlob } from "../utils/helperFunctions";
import { useUserContext } from "../contexts/UserContext";
import { remove, uploadData } from "aws-amplify/storage";
import { v4 as uuidv4 } from 'uuid';
import { appendToReportQueue, computeSummaryCounts, toAwsDateTime } from "../utils/reportUtils";
import { ShiftReportPayload } from "../types/reportTypes";
const SignatureCanvas = styled.canvas`
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  maxWidth: 400px;
  height: 200px;
  margin: 20px 0;
  background-color: white;
`;

export const ShiftReportReview = ({
    cancel,
    shiftFormPrompts,
    userLocation,
    media,
    isSending,
    onFinalSubmit,
    formId,
    shiftType = 'start-of-day',
}: iShiftReportReviewProps) => {
    const { company, companyOwner, companyUser } = useUserContext();

    const companyId = (company?.id as string) || companyOwner?.companyId;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isSubmittingRef = useRef(false);
    const [signaturePad, setSignaturePad] = useState<SignaturePad | null>(null);
    const [signatureCaptured, setSignatureCaptured] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const SAVED_SIGNATURE_KEY = `saved_signature_${companyId}`;
    const showSending = Boolean(isSending || isSubmitting);

    useEffect(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext('2d')?.scale(ratio, ratio);

            const newSignaturePad = new SignaturePad(canvas, {
                minWidth: 1,
                maxWidth: 2.5,
                penColor: "rgb(66, 133, 244)",
                backgroundColor: "rgb(255, 255, 255)"
            });
            setSignaturePad(newSignaturePad);

            // Automatically load saved signature if it exists
            const savedSignature = localStorage.getItem(SAVED_SIGNATURE_KEY);
            if (savedSignature) {
                newSignaturePad.fromDataURL(savedSignature);
                setSignatureCaptured(true); // Enable submit button immediately
            }
        }
    }, [SAVED_SIGNATURE_KEY]);

    const clearSignature = async () => {
        signaturePad?.clear();
        setSignatureCaptured(false);

        try {
            const storedSignature = localStorage.getItem(LOCAL_STORAGE_KEYS.SAFETY_SIGNATURE_PATH)

            if (storedSignature) {
                try {
                    await remove({
                        path: storedSignature,
                    })
                } catch (error) {
                    console.error("Error fetching data:", error);
                    Sentry.captureException(error);
                } finally {
                    localStorage.removeItem(LOCAL_STORAGE_KEYS.SAFETY_SIGNATURE_PATH)
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            Sentry.captureException(error);
        } finally {
            setSignatureCaptured(false)
        }
    };

    const saveSignature = async () => {
        if (signaturePad && !signaturePad.isEmpty()) {
            try {
                let imagePath;
                const signatureData = signaturePad.toDataURL('image/jpeg', 0.8);
                const blob = base64ToBlob(signatureData, 'image/jpeg');

                // Save signature to localStorage for future reuse (replaces any existing)
                localStorage.setItem(SAVED_SIGNATURE_KEY, signatureData);

                if (navigator.onLine) {
                    const signatureLabel = shiftType === 'end-of-day' ? 'END-OF-DAY' : 'START-OF-DAY';
                    const imageName = `${companyId}/${uuidv4()}-SAFETY-SIGNATURE-${signatureLabel}.jpeg`;
                    const imageFile = new File([blob as BlobPart], `${imageName}`, { type: 'image/jpeg' });

                    const savedImageS3 = await uploadData({
                        path: S3_IMAGE_PATH.signatureImagePath(companyId ?? '', imageName),
                        data: imageFile,
                    }).result;

                    imagePath = savedImageS3.path;
                    localStorage.setItem(LOCAL_STORAGE_KEYS.SAFETY_SIGNATURE_PATH, imagePath)
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setSignatureCaptured(false)
                Sentry.captureException(error);
            } finally {
                setSignatureCaptured(true)
            }
        }
    };

    const handleSubmit = async () => {
        // Sync guard: setState alone cannot block rapid double-clicks before re-render.
        if (isSubmittingRef.current || isSending || !signatureCaptured) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);

        const { okCount, warnCount, inopCount, totalItems } = computeSummaryCounts(shiftFormPrompts as any);

        // Extract locationName from the first dropdown prompt whose label contains 'location'
        const locationPrompt = shiftFormPrompts.find(
            (p: any) => p.subType === 'dropdown' && p.label?.toLowerCase().includes('location')
        );
        const locationName = (locationPrompt as any)?.value || undefined;

        const payload: ShiftReportPayload = {
            reportType: REPORT_TYPES.SHIFT as 'ShiftReport',
            shiftType,
            companyId: companyId || '',
            userId: '',
            userName: companyUser?.name || '',
            formId: formId || '',
            gpsLatitude: userLocation?.latitude,
            gpsLongitude: userLocation?.longitude,
            gpsLocationType: userLocation?.type,
            inspectionStarted: toAwsDateTime(localStorage.getItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED)),
            okCount,
            warnCount,
            inopCount,
            totalItems,
            responseData: JSON.stringify(shiftFormPrompts),
            media: JSON.stringify(media),
            alwaysTriggerEmail: true,
            locationName,
        };

        appendToReportQueue(payload);

        if (!onFinalSubmit) {
            // Queue write is done; nothing further to await. Leave locked until remount.
            return;
        }

        try {
            await onFinalSubmit();
            // Keep locked on success — callers close the modal / navigate away and remount this view.
        } catch (error) {
            console.error('Error in onFinalSubmit:', error);
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const rows = shiftFormPrompts.map((prompts, idx) => ({
        ...prompts,
        photo: (media && media[idx]?.length) || 0,
        rowKey: String(idx),
    }));

    const columns: DataTableColumn<(typeof rows)[number]>[] = [
        {
            id: "prompt",
            header: "Prompt",
            renderCell: (item) =>
                item.type === DEFAULT_FORM_INPUT_TYPES.section
                    ? `${item.title} / ${item.label}`
                    : item.label,
        },
        {
            id: "value",
            header: "Value",
            renderCell: (item) => {
                if ('selectedOptions' in item && item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions) {
                    return (
                        <div>
                            {item.selectedOptions.map((option: string, index: number) => (
                                <div key={index}>
                                    <DisplayStatusIcon size="big">{option}</DisplayStatusIcon>
                                </div>
                            ))}
                        </div>
                    );
                }
                return <DisplayStatusIcon size="big">{item.value}</DisplayStatusIcon>;
            },
        },
        {
            id: "notes",
            header: "Notes",
            renderCell: (item) => item.notes,
        },
        {
            id: "media",
            header: "Media Files",
            renderCell: (item) => item.photo,
        },
    ];

    return (
        <div className="review-report">
            <Stack size="l">
                <DataTable
                    ariaLabel="Shift report review"
                    columns={columns}
                    rows={rows}
                    getRowKey={(row) => row.rowKey}
                />
                <div className="review-report-submit">
                    <div className="review-report-signature">
                        <h2>Signature:</h2>
                        <Stack size="m">
                            <SignatureCanvas ref={canvasRef} />
                            <Stack direction="horizontal" size="xs">
                                <Btn buttonDisabled={showSending} buttonOnClick={clearSignature} buttonText="Clear Signature" />
                                <Btn
                                    buttonDisabled={signatureCaptured || showSending}
                                    buttonOnClick={saveSignature}
                                    buttonText={!signatureCaptured ? "Save Signature" : "Saved"}
                                />
                            </Stack>
                        </Stack>
                    </div>
                    <div className="review-report-actions">
                        {showSending && (
                            <TEIQspinner
                                label="Sending…"
                                orbitText="Sending…"
                                variant="md"
                            />
                        )}
                        <Stack direction="horizontal" size="l">
                            <Btn buttonDisabled={showSending} buttonOnClick={cancel} buttonText="Back" />
                            <Btn
                                variant="primary"
                                buttonOnClick={handleSubmit}
                                buttonDisabled={!signatureCaptured || showSending}
                                buttonText="Submit"
                            />
                        </Stack>
                    </div>
                </div>
            </Stack>
        </div>
    );
};
